/**
 * @fileoverview Reactivate User placement fields.
 * @description The Reactivate dialog's role and reporting-manager pickers. Presentational: the
 * choices, prefill and role-change handling are in useReactivateUserForm. The manager choices
 * re-load for the chosen role; while they load or if they fail the picker says so, and when the
 * role has no eligible manager it says that too.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import UserRequestError from '@/features/users/components/UserRequestError'
import { REACTIVATE_TEST_ID } from '@/features/users/constants/lifecycle.constants'
import SelectField from '@/shared/components/molecules/SelectField'

import type { ReactivationRoleOption } from '@/features/users/types/users.types'
import type { ReportingManagerOption } from '@/shared/types/directory-options.types'
import type { ApiError } from '@/shared/types/http.types'
import type { UseFormRegisterReturn } from 'react-hook-form'

interface ReactivationPlacementFieldsProps {
  roles: ReactivationRoleOption[]
  /** The role field's registration, with the role-change handler already attached. */
  roleRegistration: UseFormRegisterReturn
  managerRegistration: UseFormRegisterReturn
  roleError: string | undefined
  managerError: string | undefined
  managers: ReportingManagerOption[] | undefined
  managersLoaded: boolean
  managersError: ApiError | null
}

/**
 * @component
 * @param {ReactivationPlacementFieldsProps} props - The role choices, the two fields'
 * registrations and errors, and the state of the manager choices.
 * @param {ReactivationRoleOption[]} props.roles - The roles the admin may choose.
 * @param {UseFormRegisterReturn} props.roleRegistration - The role field's registration.
 * @param {UseFormRegisterReturn} props.managerRegistration - The manager field's registration.
 * @param {string | undefined} props.roleError - The role field's error, translated.
 * @param {string | undefined} props.managerError - The manager field's error, translated.
 * @param {ReportingManagerOption[] | undefined} props.managers - The role's manager choices.
 * @param {boolean} props.managersLoaded - Whether the manager choices have loaded.
 * @param {ApiError | null} props.managersError - Why they failed to load, if they did.
 */
function ReactivationPlacementFields({
  roles,
  roleRegistration,
  managerRegistration,
  roleError,
  managerError,
  managers,
  managersLoaded,
  managersError,
}: ReactivationPlacementFieldsProps) {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <SelectField
        id="reactivateUserRole"
        data-testid={`${REACTIVATE_TEST_ID}-role-select`}
        label={`${t('sales.userManagement.editUserForm.fields.role')} *`}
        options={[
          { value: '', label: t('sales.userManagement.editUserForm.fields.rolePlaceholder') },
          ...roles.map((role) => ({ value: role.roleId, label: role.name })),
        ]}
        error={roleError}
        {...roleRegistration}
      />
      <div>
        <SelectField
          id="reactivateUserReportingManager"
          data-testid={`${REACTIVATE_TEST_ID}-reporting-manager-select`}
          label={`${t('sales.userManagement.editUserForm.fields.reportsTo')} *`}
          options={[
            {
              value: '',
              label: t('sales.userManagement.editUserForm.fields.reportsToPlaceholder'),
            },
            ...(managers ?? []).map((manager) => ({
              value: manager.userId,
              label: manager.positionCode
                ? `${manager.displayName} · ${t(
                    `sales.userManagement.reactivateUser.positions.${manager.positionCode}`,
                  )}`
                : manager.displayName,
            })),
          ]}
          error={managerError}
          {...managerRegistration}
        />
        {managersLoaded && managers?.length === 0 && (
          <p
            className="mt-1.5 text-[12px] text-slate"
            data-testid={`${REACTIVATE_TEST_ID}-no-managers`}
          >
            {t('sales.userManagement.editUserForm.fields.reportsToNoneAvailable')}
          </p>
        )}
        {managersError && (
          <UserRequestError error={managersError} testIdPrefix={`${REACTIVATE_TEST_ID}-managers`} />
        )}
      </div>
    </div>
  )
}

export default ReactivationPlacementFields
